import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaList, FaGlobe, FaBuilding, FaEdit, FaTrash, FaTasks, FaUsers, FaMapMarkerAlt, FaTimes } from 'react-icons/fa';
import axios from 'axios';
import { checkIsAdmin, checkIsGlobalAdmin } from '../../utils/authUtils';
import './SystemSettings.css';
import CustomerCustomizationModal from './CustomerCustomizationModal';

const SystemSettings = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('Details');
  const [showDocTypeModal, setShowDocTypeModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showUserList, setShowUserList] = useState(false);
  const [showAddUserForm, setShowAddUserForm] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [users, setUsers] = useState([]);
  const [newUser, setNewUser] = useState({
    email: '',
    firstName: '',
    phoneNumber: '',
    roleProfile: 'User',
    password: '',
  });

  const [settings, setSettings] = useState({
    order_flow_type: 'DIRECT_SERVE', // NEW: Order Workflow
    delivery_flow_type: 'DELIVERY_NORMAL', // NEW: Delivery Workflow
    delivery_assignment_mode: 'SECOND_ASSIGN', // NEW: Delivery Assignment Mode
    country: 'India', // Updated: Default to India
    language: 'English',
    timeZone: 'Asia/Dubai',
    currency: 'INR', // Updated: Default to INR (Indian Rupee)
    dateFormat: 'yyyy/mm/dd',
    timeFormat: 'HH:mm:ss',
    numberFormat: '#,##,###.##',
    useNumberFormatFromCurrency: false,
    firstDayOfWeek: 'Monday',
    floatPrecision: 3,
    currencyPrecision: 4,
    useCurrencySymbol: false, // NEW: Toggle between Symbol and Code
    sessionExpiry: '',
    documentShareKeyExpiry: '',
    denyMultipleSessions: false,
    disableUserPassLogin: false,
    allowLoginUsingMobileNumber: false,
    allowLoginUsingUserName: false,
    loginWithEmailLink: false,
    allowConsecutiveLoginAttempts: 0,
    allowLoginAfterFail: 0,
    enableTwoFactorAuth: false,
    totalWorkingDays: 30, // NEW: Default total working days per month (kept for backend compatibility, but input removed)
    applyCompanyLeaves: false, // NEW: Checkbox to apply company leaves deduction
    voiceLanguage: 'en-US', // NEW: Default voice language
    voiceName: '', // NEW: Selected voice name
    voiceRate: 1.0, // NEW: Default voice speed
    voiceRepeatCount: 2, // NEW: Default voice repetition count
    voiceRepeatIntervalHours: 0,
    voiceRepeatIntervalMinutes: 0,
    voiceRepeatIntervalSeconds: 10,
    font_family: 'Dubai Regular', // NEW: Global font setting
    // NEW: Payment settings for different order types
    paymentSettings: {
      takeaway: { pay: true, payLater: true },
      dinein: { pay: true, payLater: true },
      onlinedelivery: { pay: true, payLater: true }
    },
    express_counter: false, // NEW: Express Counter Setting
  });

  const [availableVoices, setAvailableVoices] = useState([]);
  const [workflowSettings, setWorkflowSettings] = useState(null); // NEW: To fetch current plan modules

  const [clickCount, setClickCount] = useState(0);
  const [warningMessage, setWarningMessage] = useState('');
  const [isSuccessMessage, setIsSuccessMessage] = useState(false); // NEW: To distinguish success vs error messages
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [baseUrl, setBaseUrl] = useState("");
  const [canRead, setCanRead] = useState(false);
  const [canWrite, setCanWrite] = useState(false);
  const [permsLoading, setPermsLoading] = useState(true);
  const [showPermModal, setShowPermModal] = useState(false);
  const [permModalMsg, setPermModalMsg] = useState("");
  const [isCompanyAdmin, setIsCompanyAdmin] = useState(false);
  const [isGroupAdmin, setIsGroupAdmin] = useState(false);
  const [userBranchState, setUserBranchState] = useState("");
  const [userRole, setUserRole] = useState('');
  const [availableCompanies, setAvailableCompanies] = useState([]);
  const [availableBranches, setAvailableBranches] = useState([]);
  // UPDATED: Persist multi-selection to localStorage
  const [selectedCompanies, setSelectedCompanies] = useState(() => {
    const saved = localStorage.getItem('selectedCompanies');
    return saved ? JSON.parse(saved) : [];
  });
  const [selectedBranch, setSelectedBranch] = useState(localStorage.getItem('selectedBranch') || '');
  const [selectedBranches, setSelectedBranches] = useState(() => {
    const saved = localStorage.getItem('selectedBranches');
    return saved ? JSON.parse(saved) : [];
  });
  const [showSettingsList, setShowSettingsList] = useState(false);
  const [allSettingsHistory, setAllSettingsHistory] = useState([]);
  const [showAssignmentModal, setShowAssignmentModal] = useState(false);
  const [isLoadingList, setIsLoadingList] = useState(false);
  const [showSettingsDeleteConfirm, setShowSettingsDeleteConfirm] = useState(false);
  const [settingToDelete, setSettingToDelete] = useState(null);

  const getHeaders = () => {
    const headers = { 'Content-Type': 'application/json' };
    const userStr = localStorage.getItem('user');
    if (userStr) {
      const user = JSON.parse(userStr);
      console.log('User from LocalStorage:', user);
      const isGlobal = checkIsGlobalAdmin(user);
      const isAdmin = checkIsAdmin(user);

      // Consistent check for both 'company' and 'company_name' (backend uses 'company' at login)
      const userCompany = user.company_name || user.company;
      const companyToUse = ((isGlobal || isAdmin) && selectedCompanies.length > 0 ? selectedCompanies[0] : null) || userCompany;
      if (companyToUse) headers['X-Company-Name'] = companyToUse;

      const userBranch = user.branch_name || (user.branches && user.branches.length > 0 ? user.branches[0].branch : user.branch);
      const branchToUse = selectedBranch || userBranch;
      if (branchToUse) headers['X-Branch-Name'] = branchToUse;

      // Pass authentication context for strict endpoints (like DELETE)
      headers['X-User-Context'] = encodeURIComponent(userStr);
      const token = localStorage.getItem('token');
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
    }
    return headers;
  };

  const fetchPermissions = async (currentBaseUrl) => {
    try {
      setPermsLoading(true);
      const userStr = localStorage.getItem('user');
      if (userStr) {
        const user = JSON.parse(userStr);
        const roleRaw = user.role || user.UserType || '';
        setUserRole(roleRaw);


        const isAdminRole = checkIsAdmin(user);
        const isGroupAdminRole = checkIsGlobalAdmin(user);

        setIsCompanyAdmin(isAdminRole);
        setIsGroupAdmin(isGroupAdminRole);
        setUserBranchState(user.branch_name || user.branch || "");

        if (roleRaw) {
          const url = currentBaseUrl ? `${currentBaseUrl}/api/role-permissions?role=${encodeURIComponent(roleRaw)}` : `/api/role-permissions?role=${encodeURIComponent(roleRaw)}`;
          const response = await axios.get(url, { headers: getHeaders() });
          const perms = response.data.permissions || [];
          console.log('Received Permissions:', perms);
          const pagePerm = perms.find(p => p.pageId === 'system_settings');

          if (isAdminRole || isGroupAdminRole) {
            setCanRead(true);
            setCanWrite(true);
          } else if (pagePerm) {
            console.log('System Settings Permission:', pagePerm);
            setCanRead(pagePerm.canRead === true);
            setCanWrite(pagePerm.canWrite === true);
          }
        }
      }
    } catch (error) {
      console.error("Error fetching permissions:", error);
    } finally {
      setPermsLoading(false);
    }
  };

  const fetchBranches = async (currentBaseUrl, companyOverride) => {
    try {
      const userStr = localStorage.getItem('user');
      if (userStr) {
        const user = JSON.parse(userStr);
        const headers = {};
        const companyToUse = companyOverride || (selectedCompanies.length > 0 ? selectedCompanies[0] : null) || user.company_name;
        if (companyToUse) headers['X-Company-Name'] = companyToUse;
        const url = currentBaseUrl ? `${currentBaseUrl}/api/branches` : '/api/branches';
        const response = await axios.get(url, { headers });
        const data = response.data;
        const branchData = data.map(b => typeof b === 'string' ? b : (b.branch_name || b.branch || b.name));
        if (!branchData.includes('All Branches')) {
          branchData.unshift('All Branches');
        }
        setAvailableBranches(branchData);
      }
    } catch (error) {
      console.error('Error fetching branches:', error);
    }
  };

  const fetchCompanies = async (currentBaseUrl) => {
    try {
      const url = currentBaseUrl ? `${currentBaseUrl}/api/company-details` : '/api/company-details';
      const response = await axios.get(url, { headers: getHeaders() });
      const data = response.data;

      // FIX: Handle nested object response structure {"companyDetails": [...]}
      const details = data.companyDetails || (Array.isArray(data) ? data : []);

      let names = [];
      if (Array.isArray(details) && details.length > 0) {
        names = [...new Set(details.map(c => c.company_name).filter(Boolean))];
      }

      // Fallback to user object if no company details exist yet
      if (names.length === 0) {
        const userStr = localStorage.getItem('user');
        const userObj = userStr ? JSON.parse(userStr) : {};
        if (userObj.companies && userObj.companies.length > 0) {
          names = userObj.companies.map(c => typeof c === 'object' && c !== null ? (c.company_name || c.company || c.name || '') : c).filter(c => c && typeof c === 'string');
        } else if (userObj.company_name || userObj.company) {
          names = [userObj.company_name || userObj.company];
        }
      }

      setAvailableCompanies([...new Set(names)]);
    } catch (error) {
      console.error('Error fetching companies:', error);
    }
  };

  const fetchAllSettings = async (currentBaseUrl) => {
    try {
      setIsLoadingList(true);
      const url = currentBaseUrl ? `${currentBaseUrl}/api/settings/list` : '/api/settings/list';
      const headers = getHeaders();
      console.log('Fetching Settings List with Headers:', headers);
      const response = await fetch(url, {
        method: 'GET',
        headers: headers,
      });
      if (!response.ok) throw new Error('Failed to fetch settings list');
      const data = await response.json();
      console.log('Fetched Settings List Data:', data);
      setAllSettingsHistory(data);
    } catch (error) {
      console.error('Error fetching all settings:', error);
    } finally {
      setIsLoadingList(false);
    }
  };

  useEffect(() => {
    if (warningMessage && isSuccessMessage) {
      const timer = setTimeout(() => {
        setWarningMessage('');
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [warningMessage, isSuccessMessage]);

  // Fetch config to determine baseUrl
  const fetchConfig = async () => {
    let currentBaseUrl = "";
    try {
      const response = await axios.get("/api/network_info");
      const { config: appConfig } = response.data;
      if (appConfig.mode === "client") {
        currentBaseUrl = `http://${appConfig.server_ip}:6034`;
        setBaseUrl(currentBaseUrl);
      } else {
        setBaseUrl("");
      }
    } catch (error) {
      console.error("Failed to fetch config:", error);
      setBaseUrl("");
    } finally {
      // Fetch users and settings after determining baseUrl
      fetchPermissions(currentBaseUrl);
      fetchBranches(currentBaseUrl);
      fetchUsers(currentBaseUrl);
      fetchSettings(currentBaseUrl);

      const userStr = localStorage.getItem('user');
      if (userStr) {
        const user = JSON.parse(userStr);
        const role = (user.role || '').toLowerCase();
        if (role === 'group_admin' || role === 'superadmin' || role === 'company_admin' || role === 'admin') {
          fetchCompanies(currentBaseUrl);
        }
      }
    }
  };

  const fetchUsers = async (currentBaseUrl) => {
    try {
      const url = currentBaseUrl ? `${currentBaseUrl}/api/users` : '/api/users';
      const response = await fetch(url, {
        method: 'GET',
        headers: getHeaders(),
      });
      if (!response.ok) throw new Error('Failed to fetch users');
      const data = await response.json();
      setUsers(
        data.map((user) => ({
          fullName: user.firstName || 'Unknown',
          status: user.status || 'Active',
          userType: user.role,
          phoneNumber: user.phone_number || 'N/A',
          id: user.email,
        }))
      );
    } catch (error) {
      console.error('Error fetching users:', error);
      setWarningMessage('Failed to fetch users');
      setIsSuccessMessage(false);
    }
  };

  const fetchSettings = async (currentBaseUrl) => {
    try {
      const url = currentBaseUrl ? `${currentBaseUrl}/api/settings` : '/api/settings';
      const response = await fetch(url, {
        method: 'GET',
        headers: getHeaders(),
      });
      if (!response.ok) throw new Error('Failed to fetch settings');
      const data = await response.json();
      setSettings((prev) => ({ ...prev, ...data }));
    } catch (error) {
      console.error('Error fetching settings:', error);
      // Removed setWarningMessage to avoid unwanted overlays during rapid role switching/loading
      setIsSuccessMessage(false);
    }
  };

  const fetchWorkflowVisibility = async (currentBaseUrl) => {
    try {
      const url = currentBaseUrl ? `${currentBaseUrl}/api/workflow-visibility` : '/api/workflow-visibility';
      const response = await axios.get(url, { headers: getHeaders() });
      if (response.data) {
        setWorkflowSettings(response.data);
      }
    } catch (error) {
      console.error('Error fetching workflow visibility:', error);
    }
  };

  useEffect(() => {
    fetchConfig();
  }, []);

  useEffect(() => {
    fetchSettings(baseUrl);
    fetchUsers(baseUrl);
    fetchBranches(baseUrl);
    fetchWorkflowVisibility(baseUrl);
  }, [selectedBranch, selectedCompanies, baseUrl]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (baseUrl) {
        fetchUsers(baseUrl);
      } else {
        fetchUsers("");
      }
    }, 30000);

    // Load available voices for settings
    const loadVoices = () => {
      setAvailableVoices(window.speechSynthesis.getVoices());
    };
    loadVoices();
    if (window.speechSynthesis.onvoiceschanged !== undefined) {
      window.speechSynthesis.onvoiceschanged = loadVoices;
    }

    return () => clearInterval(interval);
  }, [baseUrl]); // Depend on baseUrl to ensure it runs after baseUrl is set

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name.includes('.')) {
      const [parent, child, grandChild] = name.split('.');
      setSettings((prev) => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: {
            ...prev[parent][child],
            [grandChild]: type === 'checkbox' ? checked : value
          }
        }
      }));
    } else {
      setSettings((prev) => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value,
      }));
    }
  };

  const handleCompanyChange = (e) => {
    const { value, checked } = e.target;
    let updated;
    if (checked) {
      updated = [...selectedCompanies, value];
    } else {
      updated = selectedCompanies.filter(c => c !== value);
    }
    setSelectedCompanies(updated);
    localStorage.setItem('selectedCompanies', JSON.stringify(updated));

    // Reset branch when context changes significantly
    setSelectedBranch('');
    localStorage.removeItem('selectedBranch');
  };

  const handleBranchChange = (e) => {
    const value = e.target.value;
    setSelectedBranch(value);
    localStorage.setItem('selectedBranch', value);
  };

  const handleBranchCheckboxChange = (e) => {
    const { value, checked } = e.target;
    let updated;
    if (checked) {
      updated = [...selectedBranches, value];
    } else {
      updated = selectedBranches.filter(b => b !== value);
    }
    setSelectedBranches(updated);
    localStorage.setItem('selectedBranches', JSON.stringify(updated));
  };

  const loadSavedSetting = (setting) => {
    // Only pick the keys that exist in the settings state default to avoid corrupting with metadata
    const sanitizedData = {};
    Object.keys(settings).forEach(key => {
      if (key in setting) sanitizedData[key] = setting[key];
    });

    setSettings(prev => ({ ...prev, ...sanitizedData }));

    if (setting.company_name) {
      const updated = [setting.company_name];
      setSelectedCompanies(updated);
      localStorage.setItem('selectedCompanies', JSON.stringify(updated));
    }
    if (setting.branch_name) {
      setSelectedBranch(setting.branch_name);
      localStorage.setItem('selectedBranch', setting.branch_name);
    }
    setShowSettingsList(false);
    setWarningMessage(`Loaded settings for ${setting.company_name || 'Global'}${setting.branch_name ? ' - ' + setting.branch_name : ''}`);
    setIsSuccessMessage(true);
  };

  const deleteSavedSetting = (item) => {
    if (!canWrite && !isCompanyAdmin && !isGroupAdmin) {
      setPermModalMsg("You do not have permission to delete settings.");
      setShowPermModal(true);
      return;
    }
    setSettingToDelete(item);
    setShowSettingsDeleteConfirm(true);
  };

  const confirmDeleteSetting = async () => {
    if (!settingToDelete) return;
    try {
      const url = baseUrl ? `${baseUrl}/api/settings/${settingToDelete._id}` : `/api/settings/${settingToDelete._id}`;
      const response = await fetch(url, {
        method: 'DELETE',
        headers: getHeaders(),
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete setting');
      }
      // Refresh the list
      fetchAllSettings(baseUrl);
      setWarningMessage(`Deleted settings successfully.`);
      setIsSuccessMessage(true);
      setShowSettingsDeleteConfirm(false);
      setSettingToDelete(null);
    } catch (error) {
      console.error('Error deleting setting:', error);
      setWarningMessage(`Failed to delete: ${error.message}`);
      setIsSuccessMessage(false);
      setShowSettingsDeleteConfirm(false);
      setSettingToDelete(null);
    }
  };

  const renderSavedSettingsList = () => (
    <div style={{ padding: '20px', background: '#F8F9FD', width: '100%', minHeight: '100vh', display: 'flex', flexDirection: 'column', boxSizing: 'border-box' }}>
      <style>
        {`
        .premium-table {
          width: 100%;
          border-collapse: separate;
          border-spacing: 0;
        }
        .premium-table th {
          background: #06D6A0;
          color: #ffffff;
          font-weight: 600;
          font-size: 15px;
          text-transform: capitalize;
          letter-spacing: 0.3px;
          padding: 16px 24px;
          text-align: left;
          border: none;
          position: sticky;
          top: 0;
          z-index: 10;
        }
        .premium-table th:first-child { border-top-left-radius: 10px; }
        .premium-table th:last-child { border-top-right-radius: 10px; }
        .premium-table td {
          padding: 16px 24px;
          color: #4A4A5A;
          font-size: 14px;
          font-weight: 500;
          border-bottom: 1px solid #F1F3F8;
          transition: all 0.2s ease;
          vertical-align: middle;
        }
        .premium-table tr:nth-child(odd) td { background: #ffffff; }
        .premium-table tr:nth-child(even) td { background: #FAFBFD; }
        .premium-table tr:hover td { background: #F1F4F9; }
        .premium-table tr:last-child td { border-bottom: none; }
        
        .action-icon-btn {
          width: 42px;
          height: 42px;
          border-radius: 10px;
          display: flex;
          justify-content: center;
          align-items: center;
          cursor: pointer;
          border: none;
          transition: all 0.2s ease;
        }
        .icon-edit { background: #fff8e1; color: #ffb300; }
        .icon-edit:hover { background: #ffecb3; }
        .icon-delete { background: #ffebee; color: #ef5350; }
        .icon-delete:hover { background: #ffcdd2; }
      `}
      </style>

      <div style={{ width: '100%' }}>
        <div style={{ marginBottom: '24px' }}>
          <h2 style={{ fontSize: '28px', fontWeight: '700', color: '#1B1B29', margin: 0, display: 'flex', alignItems: 'center', gap: '12px' }}>
            <FaList style={{ color: '#06D6A0' }} /> Saved System Settings
          </h2>
        </div>

        {isLoadingList ? (
          <div style={{ textAlign: 'center', padding: '60px', fontSize: '18px', color: '#64748b', fontWeight: '500' }}>Loading settings...</div>
        ) : (
          <div style={{ overflowX: 'auto', background: '#ffffff', borderRadius: '12px', boxShadow: '0 4px 15px rgba(0,0,0,0.02)' }}>
            <table className="premium-table">
              <thead>
                <tr>
                  <th>Company</th>
                  <th>Branch</th>
                  <th>Country</th>
                  <th>Order Workflow</th>
                  <th>Currency</th>
                  <th>Time Zone</th>
                  <th>Date Format</th>
                  <th>Time Format</th>
                  <th>Font</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {allSettingsHistory.length === 0 ? (
                  <tr><td colSpan="9" style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>No saved settings found.</td></tr>
                ) : (
                  allSettingsHistory.map((item, idx) => (
                    <tr key={idx}>
                      <td style={{ fontWeight: '600', color: '#1B1B29' }}>{item.company_name || 'Global'}</td>
                      <td>{item.branch_name || 'All Branches'}</td>
                      <td>{item.country}</td>
                      <td>
                        {item.order_flow_type === 'SEAT_ORDER_SERVE' ? 'Dining' :
                          item.order_flow_type === 'KDS_AUTO_SERVE' ? 'KDS Auto' :
                            item.order_flow_type === 'KDS_MANUAL_SERVE' ? 'KDS Manual' : 'Direct Serve'}
                      </td>
                      <td style={{ fontWeight: '600', color: '#4B76E6' }}>{item.currency}</td>
                      <td>{item.timeZone}</td>
                      <td>{item.dateFormat}</td>
                      <td>{item.timeFormat}</td>
                      <td>{item.font_family ? item.font_family.split(',')[0] : ''}</td>
                      <td>
                        <div style={{ display: 'flex', gap: '10px' }}>
                          <button className="action-icon-btn icon-edit" onClick={() => loadSavedSetting(item)} title="Edit">
                            <FaEdit size={18} />
                          </button>
                          <button className="action-icon-btn icon-delete" onClick={() => deleteSavedSetting(item)} title="Delete">
                            <FaTrash size={18} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );

  const handleGoBack = () => navigate('/admin');

  const activeBranchLocal = (localStorage.getItem('active_branch') || 'All Branches').trim();
  const isSpecificBranchActive = activeBranchLocal.toLowerCase() !== 'all branches' && activeBranchLocal.toLowerCase() !== 'all' && activeBranchLocal !== '';

  const showCompanyAssignLocal = (isGroupAdmin || isCompanyAdmin) && availableCompanies.length > 0;
  const showBranchAssignLocal = (isGroupAdmin || isCompanyAdmin) && !isSpecificBranchActive && availableBranches.length > 0;

  const handleSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!canWrite) {
      setPermModalMsg("You do not have permission to update system settings.");
      setShowPermModal(true);
      return;
    }

    if (!showAssignmentModal) {
      if (showCompanyAssignLocal || showBranchAssignLocal) {
        setShowAssignmentModal(true);
        return;
      }
    } else {
      if (selectedCompanies.length === 0 && showCompanyAssignLocal) {
        setWarningMessage("Company selection is required");
        setIsSuccessMessage(false);
        return;
      }
    }

    setShowAssignmentModal(false);

    try {
      const url = baseUrl ? `${baseUrl}/api/settings` : '/api/settings';
      
      const userStr = localStorage.getItem('user');
      const user = userStr ? JSON.parse(userStr) : {};
      const userCompany = user.company_name || user.company;

      const companiesToSave = selectedCompanies.length > 0 ? selectedCompanies : (userCompany ? [userCompany] : []);
      const defaultBranch = getHeaders()['X-Branch-Name'] || '';
      const branchesToSave = selectedBranches.length > 0 ? selectedBranches : [defaultBranch];

      setWarningMessage('Saving settings...');
      setIsSuccessMessage(true);

      let successCount = 0;
      let totalCount = Math.max(companiesToSave.length, 1) * branchesToSave.length;

      const companiesLoop = companiesToSave.length > 0 ? companiesToSave : [null]; // fallback if no company at all

      for (const company of companiesLoop) {
        for (const branch of branchesToSave) {
          const customHeaders = getHeaders();
          if (company) customHeaders['X-Company-Name'] = company;
          if (branch !== undefined) customHeaders['X-Branch-Name'] = branch; // allows empty string for branch

          const response = await fetch(url, {
            method: 'POST',
            headers: customHeaders,
            body: JSON.stringify(settings),
          });
          if (response.ok) successCount++;
        }
      }

      if (successCount === 0) {
        throw new Error('Failed to save settings');
      }

      localStorage.setItem('systemSettings', JSON.stringify(settings));
      setWarningMessage(`Settings saved successfully!`);
      setIsSuccessMessage(true);

      // If font was changed, reload to apply globally immediately
      if (activeTab === 'Font') {
        setTimeout(() => {
          window.location.reload();
        }, 1500);
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      setWarningMessage(`Failed to save settings: ${error.message}`);
      setIsSuccessMessage(false); // NEW: Mark as error
    }
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    setShowDropdown(value.length > 0);
  };

  const handleDropdownClick = () => {
    setShowUserList(true);
    setSearchQuery('');
    setShowDropdown(false);
  };

  const handleNewUserChange = (e) => {
    const { name, value } = e.target;
    setNewUser((prev) => ({ ...prev, [name]: value }));
  };

  const handleAddUser = async () => {
    if (!canWrite) {
      setPermModalMsg("You do not have permission to add users.");
      setShowPermModal(true);
      return;
    }
    if (newUser.email && newUser.firstName && newUser.phoneNumber && newUser.password) {
      const newUserData = {
        email: newUser.email,
        firstName: newUser.firstName,
        phoneNumber: newUser.phoneNumber,
        role: newUser.roleProfile.toLowerCase(),
        password: newUser.password,
        status: 'Active',
        company: 'POS 8',
        pos_profile: 'POS-001',
      };

      try {
        const url = baseUrl ? `${baseUrl}/api/register` : '/api/register';
        const response = await fetch(url, {
          method: 'POST',
          headers: getHeaders(),
          body: JSON.stringify(newUserData),
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Failed to add user');
        }

        await fetchUsers(baseUrl);
        setNewUser({ email: '', firstName: '', phoneNumber: '', roleProfile: 'User', password: '' });
        setShowAddUserForm(false);
        setWarningMessage('User added successfully! You can now login with these credentials.');
        setIsSuccessMessage(true); // NEW: Mark as success
      } catch (error) {
        console.error('Error adding user:', error);
        setWarningMessage(`Failed to add user: ${error.message}`);
        setIsSuccessMessage(false); // NEW: Mark as error
      }
    } else {
      setWarningMessage('Please fill in all required fields.');
      setIsSuccessMessage(false); // NEW: Mark as error
    }
  };

  const handleDeleteUser = (email) => {
    if (!canWrite) {
      setPermModalMsg("You do not have permission to delete users.");
      setShowPermModal(true);
      return;
    }
    setUserToDelete(email);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    try {
      const url = baseUrl ? `${baseUrl}/api/users/${userToDelete}` : `/api/users/${userToDelete}`;
      const response = await fetch(url, {
        method: 'DELETE',
        headers: getHeaders(),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to delete user');
      }

      await fetchUsers(baseUrl);
      setWarningMessage('User deleted successfully!');
      setIsSuccessMessage(true); // NEW: Mark as success
    } catch (error) {
      console.error('Error deleting user:', error);
      setWarningMessage(`Failed to delete user: ${error.message}`);
      setIsSuccessMessage(false); // NEW: Mark as error
    } finally {
      setShowDeleteConfirm(false);
      setUserToDelete(null);
    }
  };

  const dateFormatOptions = [
    'dd-mm-yyyy',
    'mm-dd-yyyy',
    'yyyy-mm-dd',
    'dd/mm/yyyy',
    'mm/dd/yyyy',
    'yyyy/mm/dd',
    'yyyy-long-mm-dd' // New: yyyy longmonth dd, e.g., 2025 October 29
  ];

  const timeFormatOptions = [
    'HH:mm:ss', // 24-hour with seconds
    'hh:mm:ss a', // 12-hour with seconds and AM/PM
    'HH:mm', // 24-hour without seconds
    'hh:mm a' // 12-hour without seconds and AM/PM
  ];

  const daysOfWeek = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const roleProfileOptions = ['User', 'Admin', 'Bearer'];

  const renderUserList = () => (
    <div className="user-list-container">
      <div className="sidebar">
        <h3>Filters</h3>
        <div>
          <label>Filter By</label>
          <input type="text" placeholder="Begin typing" />
        </div>
        <button className="edit-filters-btn">Edit Filters</button>
        <label>
          <input type="checkbox" /> Show Tags
        </label>
        <div>
          <label>Save Filter</label>
          <input type="text" placeholder="Filter Name" />
        </div>
      </div>
      <div className="main-content">
        <div className="user-list-header">
          <h3>User List</h3>
          <button className="add-user-btn" onClick={() => setShowAddUserForm(true)}>Add User</button>
        </div>
        <table>
          <thead>
            <tr>
              <th>Full Name</th>
              <th>Status</th>
              <th>User Type</th>
              <th>Phone Number</th>
              <th>ID</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((user, index) => (
              <tr key={index}>
                <td>{user.fullName}</td>
                <td>{user.status}</td>
                <td>{user.userType}</td>
                <td>{user.phoneNumber}</td>
                <td>{user.id}</td>
                <td>
                  <button className="delete-btn" onClick={() => handleDeleteUser(user.id)}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="table-footer">{users.length} of {users.length}</div>

        {showAddUserForm && (
          <div className="add-user-form">
            <h3>Add New User</h3>
            <div className="form-content">
              <div>
                <label>Email</label>
                <input
                  type="email"
                  name="email"
                  value={newUser.email}
                  onChange={handleNewUserChange}
                  placeholder="e.g., test@example.com"
                  required
                />
              </div>
              <div>
                <label>First Name</label>
                <input
                  type="text"
                  name="firstName"
                  value={newUser.firstName}
                  onChange={handleNewUserChange}
                  placeholder="e.g., Test"
                  required
                />
              </div>
              <div>
                <label>Phone Number</label>
                <input
                  type="text"
                  name="phoneNumber"
                  value={newUser.phoneNumber}
                  onChange={handleNewUserChange}
                  placeholder="e.g., 1234567890"
                  required
                />
              </div>
              <div>
                <label>Role Profile</label>
                <select name="roleProfile" value={newUser.roleProfile} onChange={handleNewUserChange}>
                  {roleProfileOptions.map((option) => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
              </div>
              <div>
                <label>Password</label>
                <input
                  type="password"
                  name="password"
                  value={newUser.password}
                  onChange={handleNewUserChange}
                  placeholder="Enter password"
                  required
                />
              </div>
            </div>
            <div className="form-buttons">
              <button className="cancel-btn" onClick={() => setShowAddUserForm(false)}>Cancel</button>
              <button className="save-btn" onClick={handleAddUser}>Save</button>
            </div>
          </div>
        )}

        <button className="back-btn" onClick={() => setShowUserList(false)}>Back to Settings</button>
      </div>
    </div>
  );

  const renderTabContent = () => {
    if (showUserList) return renderUserList();
    switch (activeTab) {
      case 'Details':
        return (
          <form onSubmit={handleSubmit} className="form-sections-wrapper">
            <div className="form-section-card theme-blue">
              <div className="section-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <div className="section-icon"><FaGlobe /></div>
                  <h2 style={{ margin: 0 }}>General Details</h2>
                </div>
                <button type="submit" className="save-btn" style={{ margin: 0 }}>Save Settings</button>
              </div>
              <div className="form-grid">
                {/* Removed inline company and branch assignments */}

                <div>
                  <label htmlFor="country">Country</label>
                  <input type="text" id="country" name="country" value={settings.country} onChange={handleInputChange} />
                </div>
                <div>
                  <label htmlFor="language">Language</label>
                  <input type="text" id="language" name="language" value={settings.language} onChange={handleInputChange} />
                </div>
                <div>
                  <label htmlFor="timeZone">Time Zone</label>
                  <input type="text" id="timeZone" name="timeZone" value={settings.timeZone} onChange={handleInputChange} />
                </div>
                <div>
                  <label htmlFor="currency">Currency</label>
                  <select id="currency" name="currency" value={settings.currency} onChange={handleInputChange}>
                    <option value="INR">INR (₹) - India</option>
                    <option value="USD">USD ($) - USA</option>
                    <option value="EUR">EUR (€) - Eurozone</option>
                    <option value="GBP">GBP (£) - UK</option>
                    <option value="AED">AED (د.إ) - UAE</option>
                    <option value="JPY">JPY (¥) - Japan</option>
                    <option value="CNY">CNY (¥) - China</option>
                    <option value="SGD">SGD ($) - Singapore</option>
                    <option value="MYR">MYR (RM) - Malaysia</option>
                    <option value="THB">THB (฿) - Thailand</option>
                    <option value="IDR">IDR (Rp) - Indonesia</option>
                    <option value="KRW">KRW (₩) - South Korea</option>
                    <option value="PHP">PHP (₱) - Philippines</option>
                    <option value="SAR">SAR (﷼) - Saudi Arabia</option>
                    <option value="QAR">QAR (﷼) - Qatar</option>
                    <option value="KWD">KWD (د.ك) - Kuwait</option>
                    <option value="OMR">OMR (﷼) - Oman</option>
                    <option value="BHD">BHD (.د.ب) - Bahrain</option>
                    <option value="CAD">CAD ($) - Canada</option>
                    <option value="AUD">AUD ($) - Australia</option>
                    <option value="NZD">NZD ($) - New Zealand</option>
                    <option value="CHF">CHF (CHF) - Switzerland</option>
                    <option value="ZAR">ZAR (R) - South Africa</option>
                    <option value="BRL">BRL (R$) - Brazil</option>
                    <option value="PKR">PKR (₨) - Pakistan</option>
                    <option value="LKR">LKR (Rs) - Sri Lanka</option>
                    <option value="NGN">NGN (₦) - Nigeria</option>
                  </select>
                </div>
                <div>
                  <label>
                    <input type="checkbox" name="useCurrencySymbol" checked={settings.useCurrencySymbol} onChange={handleInputChange} />
                    Display Currency directly as Symbol (e.g. ₹ instead of INR)
                  </label>
                </div>
                <div>
                  <label htmlFor="dateFormat">Date Format</label>
                  <select id="dateFormat" name="dateFormat" value={settings.dateFormat} onChange={handleInputChange}>
                    {dateFormatOptions.map((option) => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="timeFormat">Time Format</label>
                  <select id="timeFormat" name="timeFormat" value={settings.timeFormat} onChange={handleInputChange}>
                    {timeFormatOptions.map((option) => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="numberFormat">Number Format</label>
                  <input type="text" id="numberFormat" name="numberFormat" value={settings.numberFormat} onChange={handleInputChange} />
                </div>
                <div>
                  <label>
                    <input type="checkbox" name="useNumberFormatFromCurrency" checked={settings.useNumberFormatFromCurrency} onChange={handleInputChange} />
                    Use Number Format from Currency
                  </label>
                </div>
                <div>
                  <label htmlFor="firstDayOfWeek">First Day of the Week</label>
                  <select id="firstDayOfWeek" name="firstDayOfWeek" value={settings.firstDayOfWeek} onChange={handleInputChange}>
                    {daysOfWeek.map((day) => (
                      <option key={day} value={day}>{day}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="floatPrecision">Float Precision</label>
                  <input type="number" id="floatPrecision" name="floatPrecision" value={settings.floatPrecision} onChange={handleInputChange} min="1" max="10" />
                </div>
                <div>
                  <label htmlFor="currencyPrecision">Currency Precision</label>
                  <input type="number" id="currencyPrecision" name="currencyPrecision" value={settings.currencyPrecision} onChange={handleInputChange} placeholder="Depends on number format" />
                </div>
              </div>
            </div>
          </form>
        );
      case 'Login':
        return (
          <form onSubmit={handleSubmit} className="form-sections-wrapper">
            <div className="form-section-card theme-blue">
              <div className="section-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <div className="section-icon"><FaGlobe /></div>
                  <h2 style={{ margin: 0 }}>Login Settings</h2>
                </div>
                <button type="submit" className="save-btn" style={{ margin: 0 }}>Save Settings</button>
              </div>
              <div className="form-grid">
                <div>
                  <label htmlFor="sessionExpiry">Session Expiry (HH:MM)</label>
                  <input type="text" id="sessionExpiry" name="sessionExpiry" value={settings.sessionExpiry} onChange={handleInputChange} placeholder="e.g., 24:00" />
                  <small>Example: 24:00 logs out after 24 hours of inactivity</small>
                </div>
                <div>
                  <label htmlFor="documentShareKeyExpiry">Document Share Key Expiry (Days)</label>
                  <input type="number" id="documentShareKeyExpiry" name="documentShareKeyExpiry" value={settings.documentShareKeyExpiry} onChange={handleInputChange} min="1" />
                  <small>Days until Web View link expires</small>
                </div>
                <div>
                  <label>
                    <input type="checkbox" name="denyMultipleSessions" checked={settings.denyMultipleSessions} onChange={handleInputChange} />
                    Allow Only One Session Per User
                  </label>
                  <small>Multiple sessions allowed on mobile</small>
                </div>
                <div>
                  <label>
                    <input type="checkbox" name="disableUserPassLogin" checked={settings.disableUserPassLogin} onChange={handleInputChange} />
                    Disable Username/Password Login
                  </label>
                  <small>Configure Social Login first</small>
                </div>
                <div>
                  <label>
                    <input type="checkbox" name="allowLoginUsingMobileNumber" checked={settings.allowLoginUsingMobileNumber} onChange={handleInputChange} />
                    Allow Login Using Mobile Number
                  </label>
                </div>
                <div>
                  <label>
                    <input type="checkbox" name="allowLoginUsingUserName" checked={settings.allowLoginUsingUserName} onChange={handleInputChange} />
                    Allow Login Using User Name
                  </label>
                </div>
                <div>
                  <label>
                    <input type="checkbox" name="loginWithEmailLink" checked={settings.loginWithEmailLink} onChange={handleInputChange} />
                    Login with Email Link
                  </label>
                  <small>Passwordless login via email</small>
                </div>
                <div>
                  <label htmlFor="allowConsecutiveLoginAttempts">Allow Consecutive Login Attempts</label>
                  <input type="number" id="allowConsecutiveLoginAttempts" name="allowConsecutiveLoginAttempts" value={settings.allowConsecutiveLoginAttempts} onChange={handleInputChange} min="0" />
                </div>
                <div>
                  <label htmlFor="allowLoginAfterFail">Allow Login After Fail (Seconds)</label>
                  <input type="number" id="allowLoginAfterFail" name="allowLoginAfterFail" value={settings.allowLoginAfterFail} onChange={handleInputChange} min="0" />
                </div>
                <div>
                  <label>
                    <input type="checkbox" name="enableTwoFactorAuth" checked={settings.enableTwoFactorAuth} onChange={handleInputChange} />
                    Enable Two Factor Authentication
                  </label>
                </div>
              </div>
            </div>
          </form>
        );

      case 'Voice': // NEW: Voice Settings tab
        return (
          <form onSubmit={handleSubmit} className="form-sections-wrapper">
            <div className="form-section-card theme-blue">
              <div className="section-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <div className="section-icon"><FaGlobe /></div>
                  <h2 style={{ margin: 0 }}>Voice Settings</h2>
                </div>
                <button type="submit" className="save-btn" style={{ margin: 0 }}>Save Voice Settings</button>
              </div>
              <div className="form-grid">
                <div className="form-group" style={{ marginBottom: '20px' }}>
                  <label>Voice Language</label>
                  <select
                    name="voiceLanguage"
                    value={settings.voiceLanguage}
                    onChange={handleInputChange}
                    style={{ width: '100%', padding: '10px', borderRadius: '5px', border: '1px solid #ddd' }}
                  >
                    <option value="en-US">English (English)</option>
                    <option value="ta-IN">Tamil (தமிழ்)</option>
                    <option value="ml-IN">Malayalam (മലയാളം)</option>
                    <option value="kn-IN">Kannada (ಕನ್ನಡ)</option>
                    <option value="te-IN">Telugu (తెలుగు)</option>
                    <option value="hi-IN">Hindi (हिन्दी)</option>
                    <option value="ur-PK">Urdu (اردو)</option>
                    <option value="ar-SA">Arabic (العربية)</option>
                  </select>
                </div>

                <div style={{ marginBottom: '20px' }}>
                  <label>Voice Model</label>
                  <select
                    name="voiceName"
                    value={settings.voiceName}
                    onChange={handleInputChange}
                    style={{ width: '100%', padding: '10px', borderRadius: '5px', border: '1px solid #ddd' }}
                  >
                    <option value="">Browser Default</option>
                    {availableVoices
                      .filter(v => v.lang.startsWith(settings.voiceLanguage.split('-')[0]))
                      .map(voice => (
                        <option key={voice.name} value={voice.name}>
                          {voice.name} ({voice.lang})
                        </option>
                      ))}
                  </select>
                  <small style={{ color: '#666' }}>If no voices appear, your browser/OS might not support this language natively.</small>
                </div>

                <div style={{ marginBottom: '20px' }}>
                  <label>Voice Speed (Rate: {settings.voiceRate})</label>
                  <input
                    type="range"
                    name="voiceRate"
                    min="0.5"
                    max="1.5"
                    step="0.1"
                    value={settings.voiceRate}
                    onChange={handleInputChange}
                    style={{ width: '100%', cursor: 'pointer' }}
                  />
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', color: '#666' }}>
                    <span>Slow</span>
                    <span>Normal</span>
                    <span>Fast</span>
                  </div>
                </div>

                <div style={{ marginBottom: '20px' }}>
                  <label>Voice Repetition Count</label>
                  <input
                    type="number"
                    name="voiceRepeatCount"
                    min="1"
                    max="10"
                    value={settings.voiceRepeatCount}
                    onChange={handleInputChange}
                    style={{ width: '100%', padding: '10px', borderRadius: '5px', border: '1px solid #ddd' }}
                  />
                  <small style={{ color: '#666' }}>How many times the order announcement should be repeated.</small>
                </div>

                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '8px' }}>Repetition Interval Delay</label>
                  <div style={{ display: 'flex', gap: '15px' }}>
                    <div style={{ flex: 1 }}>
                      <label style={{ fontSize: '12px', color: '#666' }}>Hours</label>
                      <input
                        type="number"
                        name="voiceRepeatIntervalHours"
                        min="0"
                        max="23"
                        value={settings.voiceRepeatIntervalHours || 0}
                        onChange={handleInputChange}
                        style={{ width: '100%', padding: '10px', borderRadius: '5px', border: '1px solid #ddd' }}
                      />
                    </div>
                    <div style={{ flex: 1 }}>
                      <label style={{ fontSize: '12px', color: '#666' }}>Minutes</label>
                      <input
                        type="number"
                        name="voiceRepeatIntervalMinutes"
                        min="0"
                        max="59"
                        value={settings.voiceRepeatIntervalMinutes || 0}
                        onChange={handleInputChange}
                        style={{ width: '100%', padding: '10px', borderRadius: '5px', border: '1px solid #ddd' }}
                      />
                    </div>
                    <div style={{ flex: 1 }}>
                      <label style={{ fontSize: '12px', color: '#666' }}>Seconds</label>
                      <input
                        type="number"
                        name="voiceRepeatIntervalSeconds"
                        min="0"
                        max="59"
                        value={settings.voiceRepeatIntervalSeconds || 10}
                        onChange={handleInputChange}
                        style={{ width: '100%', padding: '10px', borderRadius: '5px', border: '1px solid #ddd' }}
                      />
                    </div>
                  </div>
                  <small style={{ color: '#666', display: 'block', marginTop: '5px' }}>Set the specific time interval between each voice repetition.</small>
                </div>

                <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                  <button
                    type="button"
                    onClick={() => {
                      const utterance = new SpeechSynthesisUtterance("This is a test announcement.");
                      const voice = availableVoices.find(v => v.name === settings.voiceName);
                      if (voice) utterance.voice = voice;
                      utterance.lang = settings.voiceLanguage;
                      utterance.rate = parseFloat(settings.voiceRate) || 1.0;
                      window.speechSynthesis.speak(utterance);
                    }}
                    className="test-voice-btn"
                    style={{
                      flex: 1,
                      padding: '12px',
                      backgroundColor: '#f1f1f1',
                      border: '1px solid #ccc',
                      borderRadius: '5px',
                      cursor: 'pointer'
                    }}
                  >
                    Test Voice
                  </button>
                </div>
              </div>
            </div>
          </form>
        );
      case 'Font': // NEW: Font Settings tab
        return (
          <form onSubmit={handleSubmit} className="form-sections-wrapper">
            <div className="form-section-card theme-blue">
              <div className="section-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <div className="section-icon"><FaGlobe /></div>
                  <h2 style={{ margin: 0 }}>Font Settings</h2>
                </div>
                <button type="submit" className="save-btn" style={{ margin: 0 }}>Save Font Settings</button>
              </div>
              <div className="form-grid">
                <div style={{ marginBottom: '20px' }}>
                  <label htmlFor="font_family">Select Application Font</label>
                  <select
                    id="font_family"
                    name="font_family"
                    value={settings.font_family}
                    onChange={handleInputChange}
                    style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1.5px solid #e0e0e0', fontSize: '1rem', appearance: 'none', background: 'white' }}
                  >
                    <option value="Dubai Regular" style={{ fontFamily: 'Dubai Regular' }}>Dubai Regular (Default)</option>
                    <option value="'Roboto', sans-serif" style={{ fontFamily: 'Roboto' }}>Roboto</option>
                    <option value="'Open Sans', sans-serif" style={{ fontFamily: 'Open Sans' }}>Open Sans</option>
                    <option value="'Montserrat', sans-serif" style={{ fontFamily: 'Montserrat' }}>Montserrat</option>
                    <option value="'Poppins', sans-serif" style={{ fontFamily: 'Poppins' }}>Poppins</option>
                    <option value="'Inter', sans-serif" style={{ fontFamily: 'Inter' }}>Inter</option>
                    <option value="Arial, sans-serif" style={{ fontFamily: 'Arial' }}>Arial</option>
                    <option value="Georgia, serif" style={{ fontFamily: 'Georgia' }}>Georgia</option>
                    <option value="system-ui, -apple-system, sans-serif">System Default</option>
                  </select>
                  <small style={{ color: '#666', marginTop: '10px', display: 'block' }}>
                    This font will be applied globally across all pages and reports.
                  </small>
                </div>

                <div style={{
                  marginTop: '30px',
                  padding: '20px',
                  border: '1px dashed #ccc',
                  borderRadius: '8px',
                  fontFamily: settings.font_family,
                  backgroundColor: '#f9f9f9'
                }}>
                  <h4 style={{ marginBottom: '10px' }}>Font Preview</h4>
                  <p>The quick brown fox jumps over the lazy dog.</p>
                  <p style={{ fontWeight: 'bold' }}>Bold: The quick brown fox jumps over the lazy dog.</p>
                  <p style={{ fontStyle: 'italic' }}>Italic: The quick brown fox jumps over the lazy dog.</p>
                </div>
              </div>
            </div>
          </form>
        );
      case 'Payment':
        return (
          <div className="form-sections-wrapper">
            <div className="form-section-card theme-blue">
              <div className="section-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <div className="section-icon"><FaGlobe /></div>
                  <h2 style={{ margin: 0 }}>Payment Settings</h2>
                </div>
                <button onClick={handleSubmit} className="save-btn" style={{ margin: 0 }}>Save Payment Settings</button>
              </div>
              <div className="form-grid">
                <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '20px' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f8f9fa' }}>
                      <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Order Type</th>
                      <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #dee2e6' }}>Pay Button</th>
                      <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #dee2e6' }}>Pay Later Button</th>
                    </tr>
                  </thead>
                  <tbody>
                    {['takeaway', 'dinein', 'onlinedelivery'].map((type) => (
                      <tr key={type} style={{ borderBottom: '1px solid #eee' }}>
                        <td style={{ padding: '12px', fontWeight: '500', textTransform: 'capitalize' }}>
                          {type === 'onlinedelivery' ? 'Online Delivery' : type === 'dinein' ? 'Dine In' : type}
                        </td>
                        <td style={{ padding: '12px', textAlign: 'center' }}>
                          <input
                            type="checkbox"
                            name={`paymentSettings.${type}.pay`}
                            checked={settings.paymentSettings[type].pay}
                            onChange={handleInputChange}
                            style={{ width: '20px', height: '20px', cursor: 'pointer' }}
                          />
                        </td>
                        <td style={{ padding: '12px', textAlign: 'center' }}>
                          <input
                            type="checkbox"
                            name={`paymentSettings.${type}.payLater`}
                            checked={settings.paymentSettings[type].payLater}
                            onChange={handleInputChange}
                            style={{ width: '20px', height: '20px', cursor: 'pointer' }}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        );
      case 'Work Flow':
        const hasKitchen = workflowSettings?.global_modules?.kitchen_display?.enabled !== false;
        const hasTable = workflowSettings?.global_modules?.table_management?.enabled !== false;

        return (
          <form onSubmit={handleSubmit} className="form-sections-wrapper">
            <div className="form-section-card theme-blue">
              <div className="section-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <div className="section-icon"><FaTasks /></div>
                  <h2 style={{ margin: 0 }}>Workflow Settings</h2>
                </div>
                <button type="submit" className="save-btn" style={{ margin: 0 }}>Save Settings</button>
              </div>

              {/* Order Workflow */}
              <div style={{ padding: '20px', background: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0', marginTop: '20px' }}>
                <h3 style={{ marginBottom: '15px', color: '#1e293b', fontSize: '16px', fontWeight: '600' }}>Select Dine-In/Takeaway Flow</h3>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '15px', border: '1px solid #e2e8f0', borderRadius: '8px', cursor: 'pointer', background: settings.order_flow_type === 'DIRECT_SERVE' ? '#f0f9ff' : '#fff', transition: 'all 0.2s' }}>
                    <input type="radio" name="order_flow_type" value="DIRECT_SERVE" checked={settings.order_flow_type === 'DIRECT_SERVE'} onChange={handleInputChange} style={{ width: '18px', height: '18px', accentColor: '#3b82f6' }} />
                    <span style={{ fontSize: '15px', fontWeight: '500', color: '#334155' }}>Order → Serve → Bill (Direct Serve)</span>
                  </label>

                  {hasTable && (
                    <label style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '15px', border: '1px solid #e2e8f0', borderRadius: '8px', cursor: 'pointer', background: settings.order_flow_type === 'SEAT_ORDER_SERVE' ? '#f0f9ff' : '#fff', transition: 'all 0.2s' }}>
                      <input type="radio" name="order_flow_type" value="SEAT_ORDER_SERVE" checked={settings.order_flow_type === 'SEAT_ORDER_SERVE'} onChange={handleInputChange} style={{ width: '18px', height: '18px', accentColor: '#3b82f6' }} />
                      <span style={{ fontSize: '15px', fontWeight: '500', color: '#334155' }}>Seat → Order → Serve → Bill (Seat & Serve)</span>
                    </label>
                  )}

                  {hasKitchen && (
                    <>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '15px', border: '1px solid #e2e8f0', borderRadius: '8px', cursor: 'pointer', background: settings.order_flow_type === 'KDS_AUTO_SERVE' ? '#f0f9ff' : '#fff', transition: 'all 0.2s' }}>
                        <input type="radio" name="order_flow_type" value="KDS_AUTO_SERVE" checked={settings.order_flow_type === 'KDS_AUTO_SERVE'} onChange={handleInputChange} style={{ width: '18px', height: '18px', accentColor: '#3b82f6' }} />
                        <span style={{ fontSize: '15px', fontWeight: '500', color: '#334155' }}>Order → Kitchen → Prepared → Serve → Bill</span>
                      </label>

                      <label style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '15px', border: '1px solid #e2e8f0', borderRadius: '8px', cursor: 'pointer', background: settings.order_flow_type === 'KDS_MANUAL_SERVE' ? '#f0f9ff' : '#fff', transition: 'all 0.2s' }}>
                        <input type="radio" name="order_flow_type" value="KDS_MANUAL_SERVE" checked={settings.order_flow_type === 'KDS_MANUAL_SERVE'} onChange={handleInputChange} style={{ width: '18px', height: '18px', accentColor: '#3b82f6' }} />
                        <span style={{ fontSize: '15px', fontWeight: '500', color: '#334155' }}>Order → Kitchen → Prepared → Served OK → Bill</span>
                      </label>
                    </>
                  )}
                </div>
              </div>

              {/* Delivery Workflow */}
              <div style={{ padding: '20px', background: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0', marginTop: '20px' }}>
                <h3 style={{ marginBottom: '15px', color: '#1e293b', fontSize: '16px', fontWeight: '600' }}>Select Delivery Flow</h3>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '15px', border: '1px solid #e2e8f0', borderRadius: '8px', cursor: 'pointer', background: settings.delivery_flow_type === 'DELIVERY_NORMAL' ? '#f0f9ff' : '#fff', transition: 'all 0.2s' }}>
                    <input type="radio" name="delivery_flow_type" value="DELIVERY_NORMAL" checked={settings.delivery_flow_type === 'DELIVERY_NORMAL'} onChange={handleInputChange} style={{ width: '18px', height: '18px', accentColor: '#3b82f6' }} />
                    <span style={{ fontSize: '15px', fontWeight: '500', color: '#334155' }}>Order → Bill → Delivery (No Assignment)</span>
                  </label>

                  <label style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '15px', border: '1px solid #e2e8f0', borderRadius: '8px', cursor: 'pointer', background: settings.delivery_flow_type === 'DELIVERY_ENHANCE' ? '#f0f9ff' : '#fff', transition: 'all 0.2s' }}>
                    <input type="radio" name="delivery_flow_type" value="DELIVERY_ENHANCE" checked={settings.delivery_flow_type === 'DELIVERY_ENHANCE'} onChange={handleInputChange} style={{ width: '18px', height: '18px', accentColor: '#3b82f6' }} />
                    <span style={{ fontSize: '15px', fontWeight: '500', color: '#334155' }}>Order → Assign Delivery → Accept (Secret Key) → Complete</span>
                  </label>

                  {hasKitchen && (
                    <label style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '15px', border: '1px solid #e2e8f0', borderRadius: '8px', cursor: 'pointer', background: settings.delivery_flow_type === 'DELIVERY_AT_MOST' ? '#f0f9ff' : '#fff', transition: 'all 0.2s' }}>
                      <input type="radio" name="delivery_flow_type" value="DELIVERY_AT_MOST" checked={settings.delivery_flow_type === 'DELIVERY_AT_MOST'} onChange={handleInputChange} style={{ width: '18px', height: '18px', accentColor: '#3b82f6' }} />
                      <span style={{ fontSize: '15px', fontWeight: '500', color: '#334155' }}>Order → KOT → Prepared → Assign Delivery → Accept (Secret Key) → Complete</span>
                    </label>
                  )}
                </div>
              </div>

              {/* Delivery Assignment Mode */}
              <div style={{ padding: '20px', background: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0', marginTop: '20px' }}>
                <h3 style={{ marginBottom: '15px', color: '#1e293b', fontSize: '16px', fontWeight: '600' }}>Delivery Assignment Mode</h3>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '15px', border: '1px solid #e2e8f0', borderRadius: '8px', cursor: 'pointer', background: settings.delivery_assignment_mode === 'SECOND_ASSIGN' ? '#f0f9ff' : '#fff', transition: 'all 0.2s' }}>
                    <input type="radio" name="delivery_assignment_mode" value="SECOND_ASSIGN" checked={settings.delivery_assignment_mode === 'SECOND_ASSIGN' || !settings.delivery_assignment_mode} onChange={handleInputChange} style={{ width: '18px', height: '18px', accentColor: '#3b82f6' }} />
                    <span style={{ fontSize: '15px', fontWeight: '500', color: '#334155' }}>Second Assign (Manual)</span>
                  </label>

                  <label style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '15px', border: '1px solid #e2e8f0', borderRadius: '8px', cursor: 'pointer', background: settings.delivery_assignment_mode === 'POOLING' ? '#f0f9ff' : '#fff', transition: 'all 0.2s' }}>
                    <input type="radio" name="delivery_assignment_mode" value="POOLING" checked={settings.delivery_assignment_mode === 'POOLING'} onChange={handleInputChange} style={{ width: '18px', height: '18px', accentColor: '#3b82f6' }} />
                    <span style={{ fontSize: '15px', fontWeight: '500', color: '#334155' }}>Function Pooling (Auto-Broadcast)</span>
                  </label>
                </div>
              </div>
            </div>
          </form>
        );
      case 'Counter':
        return (
          <form onSubmit={handleSubmit} className="form-sections-wrapper">
            <div className="form-section-card theme-blue">
              <div className="section-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <div className="section-icon"><FaTasks /></div>
                  <h2 style={{ margin: 0 }}>Counter Settings</h2>
                </div>
                <button type="submit" className="save-btn" style={{ margin: 0 }}>Save Settings</button>
              </div>

              <div style={{ padding: '20px', background: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0', marginTop: '20px' }}>
                <h3 style={{ marginBottom: '15px', color: '#1e293b', fontSize: '16px', fontWeight: '600' }}>Express Counter Flow</h3>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '15px', border: '1px solid #e2e8f0', borderRadius: '8px', cursor: 'pointer', background: settings.express_counter ? '#f0f9ff' : '#fff', transition: 'all 0.2s' }}>
                    <input type="checkbox" name="express_counter" checked={settings.express_counter} onChange={handleInputChange} style={{ width: '18px', height: '18px', accentColor: '#3b82f6' }} />
                    <span style={{ fontSize: '15px', fontWeight: '500', color: '#334155' }}>Enable Express Counter (Direct Cash/Card/UPI Buttons on POS)</span>
                  </label>
                </div>
              </div>
            </div>
          </form>
        );
      default:
        return <div className="coming-soon">Coming soon...</div>;
    }
  };

  const tabs = ['Details', 'Work Flow', 'Counter', 'Login', 'Voice', 'Font', 'Payment']; // UPDATED: Added 'Counter'

  if (permsLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', background: 'linear-gradient(135deg, #ffffff 0%, #3498db 100%)' }}>
        <div style={{ textAlign: 'center', color: '#3498db', fontSize: '18px' }}>
          <p>Loading Permissions...</p>
        </div>
      </div>
    );
  }

  if (!canRead && !isCompanyAdmin && !isGroupAdmin && !permsLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', background: 'linear-gradient(135deg, #ffffff 0%, #3498db 100%)' }}>
        <div style={{ textAlign: 'center', color: '#e74c3c', fontSize: '18px', padding: '30px', background: '#fff', borderRadius: '15px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
          <h2 style={{ marginBottom: '10px' }}>Access Denied</h2>
          <p>You do not have permission to view System Settings.</p>
          <button
            className="btn btn-primary"
            onClick={() => navigate('/admin')}
            style={{ marginTop: '20px', padding: '10px 20px', borderRadius: '50px', background: '#3498db', color: '#fff', border: 'none', cursor: 'pointer' }}
          >
            Back to Admin
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="system-settings-container main-customer-page">
      {/* Centered Permission Denied Modal */}
      {showPermModal && (
        <div className="warning-modal-overlay">
          <div className="warning-modal" style={{ textAlign: 'center', padding: '30px' }}>
            <div style={{ color: '#e74c3c', fontSize: '3rem', marginBottom: '15px' }}>⚠️</div>
            <h3 style={{ marginBottom: '10px', color: '#2c3e50' }}>Permission Denied</h3>
            <p style={{ color: '#7f8c8d', marginBottom: '20px' }}>{permModalMsg}</p>
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <button
                className="save-btn"
                onClick={() => setShowPermModal(false)}
              >
                Understood
              </button>
            </div>
          </div>
        </div>
      )}
      <div className="customer-header-section">
        <div className="header-left">
          <button onClick={handleGoBack} className="header-back-btn">
            <FaArrowLeft /> Back to Admin
          </button>
        </div>
        <div className="header-center">
          <h1>System Settings</h1>
        </div>
        <div className="header-right">
          <div className="header-actions">
            {isGroupAdmin && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <FaBuilding style={{ color: '#3498db' }} />
                <label style={{ fontWeight: '600', color: '#2c3e50', fontSize: '14px' }}>Companies:</label>
                <div style={{
                  padding: '8px 12px',
                  borderRadius: '8px',
                  border: '1.5px solid #e0e0e0',
                  background: '#f8f9fa',
                  fontSize: '13px',
                  color: '#666',
                  maxWidth: '250px',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}>
                  {selectedCompanies.length === 0 ? "Global / None" : selectedCompanies.join(', ')}
                </div>
              </div>
            )}

            {showSettingsList ? (
              <button
                className="header-back-btn"
                onClick={() => setShowSettingsList(false)}
              >
                <FaArrowLeft /> Back to Edit
              </button>
            ) : (
              <button
                className="header-back-btn"
                onClick={() => { setShowSettingsList(true); fetchAllSettings(baseUrl); }}
              >
                <FaList /> List View
              </button>
            )}

            {(isGroupAdmin || (isCompanyAdmin && !userBranchState)) && (
              <button
                className="add-btn"
                onClick={() => setShowDocTypeModal(true)}
                style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
              >
                <FaEdit /> Customize
              </button>
            )}

            {showCompanyAssignLocal && (
              <button
                type="button"
                className="customize-btn"
                onClick={() => setShowAssignmentModal(true)}
                style={{
                  background: '#eff2f9', color: '#604BE8', border: '1.5px solid #C3CDE4',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '0 24px', height: '42px',
                  borderRadius: '10px', fontWeight: '700', fontSize: '14px', cursor: 'pointer', transition: 'all 0.2s ease', marginLeft: '10px'
                }}
              >
                <FaUsers /> Company
              </button>
            )}

            {showBranchAssignLocal && (
              <button
                type="button"
                className="customize-btn branch-btn"
                onClick={() => setShowAssignmentModal(true)}
                style={{
                  background: '#fff8f0', color: '#FF9F04', border: '1.5px solid #FDD36D',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '0 24px', height: '42px',
                  borderRadius: '10px', fontWeight: '700', fontSize: '14px', cursor: 'pointer', transition: 'all 0.2s ease', marginLeft: '10px'
                }}
              >
                <FaMapMarkerAlt /> Branches
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="customer-scroll-body">
        <div className="main-content-card">

          {/* Search Container */}
          <div className="search-container">
            <input
              type="text"
              value={searchQuery}
              onChange={handleSearchChange}
              placeholder="Search (e.g., userlist)"
            />
            {showDropdown && (
              <div className="dropdown">
                <div className="dropdown-item" onClick={handleDropdownClick}>User List</div>
              </div>
            )}
          </div>

          {warningMessage && (
            <div className="warning-modal-overlay">
              <div className="warning-modal" style={{ padding: '30px', textAlign: 'center' }}>
                <div style={{ fontSize: "40px", marginBottom: "15px" }}>{isSuccessMessage ? "✅" : "❌"}</div>
                <p className="message-text" style={{ margin: '20px 0', fontSize: '18px', fontWeight: '700', color: isSuccessMessage ? '#10b981' : '#ef4444' }}>
                  {warningMessage}
                </p>
                <button className="save-btn" onClick={() => setWarningMessage('')} style={{ margin: '0 auto' }}>
                  Close
                </button>
              </div>
            </div>
          )}

          {!showUserList && !showSettingsList && (
            <div className="tabs" style={{ display: 'flex', gap: '12px', marginBottom: '30px', borderBottom: '2px solid #e2e8f0', paddingBottom: '15px', flexWrap: 'wrap' }}>
              {tabs.map((tab) => (
                <button
                  key={tab}
                  className={`tab-btn ${activeTab === tab ? 'active' : ''}`}
                  style={{
                    background: activeTab === tab ? 'var(--primary-color)' : '#ffffff',
                    color: activeTab === tab ? '#ffffff' : '#64748b',
                    border: activeTab === tab ? 'none' : '1px solid #e2e8f0',
                    padding: '10px 24px',
                    borderRadius: '12px',
                    fontWeight: '700',
                    fontSize: '15px',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    boxShadow: activeTab === tab ? '0 4px 10px rgba(96, 75, 232, 0.2)' : 'none'
                  }}
                  onClick={() => setActiveTab(tab)}
                >
                  {tab}
                </button>
              ))}
            </div>
          )}

          <div className="form-sections-wrapper">
            {showSettingsList ? renderSavedSettingsList() : renderTabContent()}
          </div>

          {showDeleteConfirm && (
            <>
              <div className="modal-overlay" onClick={() => setShowDeleteConfirm(false)} />
              <div className="confirm-modal">
                <p className="confirm-text">Are you sure you want to delete user {userToDelete}?</p>
                <div className="modal-button-group">
                  <button className="confirm-delete-btn" onClick={confirmDelete}>
                    Yes, Delete
                  </button>
                  <button className="cancel-delete-btn" onClick={() => setShowDeleteConfirm(false)}>
                    No, Cancel
                  </button>
                </div>
              </div>
            </>
          )}

          {showSettingsDeleteConfirm && settingToDelete && (
            <div style={{
              position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
              backgroundColor: 'rgba(27, 27, 41, 0.5)', display: 'flex', justifyContent: 'center',
              alignItems: 'center', zIndex: 10001, backdropFilter: 'blur(8px)'
            }}>
              <div style={{
                backgroundColor: '#ffffff', borderRadius: '20px', width: '420px', maxWidth: '90%',
                padding: '36px', boxShadow: '0 20px 50px rgba(0, 0, 0, 0.2)',
                textAlign: 'center', animation: 'fadeInScale 0.3s ease-out', color: '#1B1B29'
              }}>
                <div style={{ width: '70px', height: '70px', background: '#fee2e2', color: '#FF595E', borderRadius: '18px', display: 'flex', justifyContent: 'center', alignItems: 'center', fontSize: '28px', margin: '0 auto 20px' }}>
                  <FaTrash />
                </div>
                <h3 style={{ color: '#1B1B29', fontSize: '22px', fontWeight: '700', marginBottom: '10px' }}>Remove Settings?</h3>
                <p style={{ color: '#777777', fontSize: '15px', marginBottom: '30px', lineHeight: '1.6', fontWeight: '500' }}>
                  Are you sure you want to delete the settings for <strong style={{ color: '#1B1B29' }}>{settingToDelete.company_name || 'Global'} - {settingToDelete.branch_name || 'All Branches'}</strong>? This action is permanent.
                </p>
                <div style={{ display: 'flex', gap: '12px' }}>
                  <button onClick={() => setShowSettingsDeleteConfirm(false)} style={{ flex: 1, padding: '14px', borderRadius: '10px', border: '1px solid #E2E6EF', background: '#ffffff', color: '#1B1B29', fontWeight: '600', fontSize: '14px', cursor: 'pointer' }}>
                    Cancel
                  </button>
                  <button onClick={confirmDeleteSetting} style={{ flex: 1, padding: '14px', borderRadius: '10px', border: 'none', background: '#FF595E', color: '#ffffff', fontWeight: '600', fontSize: '14px', cursor: 'pointer', boxShadow: '0 4px 15px rgba(255, 89, 94, 0.3)' }}>
                    Delete
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      {/* Assignment Modal */}
      {showAssignmentModal && (
        <div className="add-modal-overlay">
          <div className="add-modal" style={{ maxWidth: '600px', width: '90%', padding: 0 }}>
            <div className="modal-header" style={{ padding: '20px', borderBottom: '1px solid #e0e0e0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: '18px', fontWeight: '700', color: '#1e293b' }}>Settings Target Assignments</span>
              <button className="close-modal-btn" onClick={() => setShowAssignmentModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '20px', color: '#64748b' }}><FaTimes /></button>
            </div>
            <div className="modal-body" style={{ padding: '20px' }}>
              {showCompanyAssignLocal && (
                <div className="assignment-card" style={{ marginBottom: '20px' }}>
                  <h4 style={{ color: '#604BE8', marginBottom: '15px', display: 'flex', alignItems: 'center', gap: '8px', margin: 0 }}><FaUsers /> Select Company *</h4>
                  <div className="checkbox-group" style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', marginTop: '15px' }}>
                    {availableCompanies.map((comp, idx) => (
                      <label key={idx} className="checkbox-label" style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 16px', background: selectedCompanies.includes(comp) ? '#e6f7ff' : '#f8fafc', borderRadius: '10px', border: '1.5px solid', borderColor: selectedCompanies.includes(comp) ? '#604BE8' : '#e2e8f0', cursor: 'pointer', fontSize: '14px', fontWeight: '600', transition: 'all 0.2s' }}>
                        <input type="checkbox" checked={selectedCompanies.includes(comp)} onChange={(e) => { const updated = e.target.checked ? [...selectedCompanies, comp] : selectedCompanies.filter(c => c !== comp); setSelectedCompanies(updated); localStorage.setItem('selectedCompanies', JSON.stringify(updated)); }} style={{ width: '16px', height: '16px', accentColor: '#604BE8' }} />
                        {comp}
                      </label>
                    ))}
                  </div>
                </div>
              )}
              {showBranchAssignLocal && (
                <div className="assignment-card">
                  <h4 style={{ color: '#06D6A0', marginBottom: '15px', display: 'flex', alignItems: 'center', gap: '8px', margin: 0 }}><FaMapMarkerAlt /> Select Branch</h4>
                  <div className="checkbox-group" style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', marginTop: '15px' }}>
                    {availableBranches.map((branch, idx) => (
                      <label key={idx} className="checkbox-label" style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 16px', background: selectedBranches.includes(branch) ? '#ecfdf5' : '#f8fafc', borderRadius: '10px', border: '1.5px solid', borderColor: selectedBranches.includes(branch) ? '#06D6A0' : '#e2e8f0', cursor: 'pointer', fontSize: '14px', fontWeight: '600', transition: 'all 0.2s' }}>
                        <input type="checkbox" checked={selectedBranches.includes(branch)} onChange={(e) => { const updated = e.target.checked ? [...selectedBranches, branch] : selectedBranches.filter(b => b !== branch); setSelectedBranches(updated); localStorage.setItem('selectedBranches', JSON.stringify(updated)); }} style={{ width: '16px', height: '16px', accentColor: '#06D6A0' }} />
                        {branch}
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="modal-footer" style={{ padding: '20px', borderTop: '1px solid #e0e0e0', display: 'flex', justifyContent: 'flex-end' }}>
              <button className="save-btn" onClick={handleSubmit} style={{ margin: 0, padding: '12px 24px' }}>
                Confirm & Save
              </button>
            </div>
          </div>
        </div>
      )}

      <CustomerCustomizationModal
        isOpen={showDocTypeModal}
        onClose={() => setShowDocTypeModal(false)}
        targetDocType="Global Settings"
      />
    </div>
  );
};

export default SystemSettings;
